import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { fullStudentSchema, studentYearNullableSchema } from './student.js';
import { schoolWithOrgSchema } from './school-shared.js';
import { adminSessionSchema } from './session.js';
import { filteringSchemaFrom, pagingSchema, sortingSchemaFrom, sortOrderSchemaFrom } from './modifier.js';
import { paginatedResponse } from './util-schema-types.js';

export const plainSchoolReportSchema = z.object({
  id: z.string(),
  startedAtInstant: z.instanceof(Temporal.Instant),
  endedAtInstant: z.instanceof(Temporal.Instant),
  grade: studentYearNullableSchema,
  schoolId: z.string(),
  studentId: z.string(),
  report: z.string(),
  reportFromAI: z.string().nullable(),
  reportFromAISpanish: z.string().nullable(),
});
export type PlainSchoolReport = z.infer<typeof plainSchoolReportSchema>;

export const schoolReportSchema = plainSchoolReportSchema.extend({
  school: schoolWithOrgSchema,
  student: fullStudentSchema,
  sessions: z.array(adminSessionSchema),
  floridaStandards: z.string(),
  arkansasStandards: z.string(),
});
export type SchoolReport = z.infer<typeof schoolReportSchema>;

export const schoolReportForAISchema = z.object({
  id: z.string(),
  report: z.string(),
  reportFromAI: z.string().nullable(),
  reportFromAISpanish: z.string().nullable(),
  studentId: z.string(),
  student: z.object({
    firstName: z.string().nullable(),
    learningPath: z.object({ id: z.string() }).nullable(),
    startingLesson: z.object({ id: z.string() }).nullable(),
  }),
  schoolId: z.string(),
  school: z.object({
    id: z.string(),
    name: z.string(),
    needSpanishFeedback: z.boolean(),
  }),
  sessions: z.array(z.object({
    id: z.string(),
    startInstant: z.instanceof(Temporal.Instant),
    lesson: z.object({ name: z.string() }),
    learningPath: z.object({ name: z.string() }).nullable(),
  })),
});
export type SchoolReportForAI = z.infer<typeof schoolReportForAISchema>;

export const schoolReportItemSchema = z.object({
  grade: studentYearNullableSchema,
  session: adminSessionSchema,
  message: z.string(),
});
export type SchoolReportItem = z.infer<typeof schoolReportItemSchema>;

export const getSchoolReportsSortableColumnSchema = schoolReportSchema.keyof().extract(['startedAtInstant']);
export type GetSchoolReportsSortableColumn = z.infer<typeof getSchoolReportsSortableColumnSchema>;

export const getSchoolReportsSortingSchema = sortingSchemaFrom(getSchoolReportsSortableColumnSchema);

export const getSchoolReportsSortOrderSchema = sortOrderSchemaFrom(getSchoolReportsSortableColumnSchema);
export type GetSchoolReportsSortOrder = z.infer<typeof getSchoolReportsSortOrderSchema>;

export const getSchoolReportsFilterSchema = z.object({
  query: z.string().optional(),
  startOnOrAfter: z.string().optional(),
  startOnOrBefore: z.string().optional(),
  grades: z.array(studentYearNullableSchema).optional(),
  schools: z.string().array().optional(),
});
export type GetSchoolReportsFilter = z.infer<typeof getSchoolReportsFilterSchema>;

export const getSchoolReportsModifierSchema = pagingSchema
  .merge(filteringSchemaFrom(getSchoolReportsFilterSchema))
  .merge(getSchoolReportsSortingSchema);
export type GetSchoolReportsModifier = z.infer<typeof getSchoolReportsModifierSchema>;

export const getSchoolReportsCsvModifierSchema = getSchoolReportsModifierSchema
  .omit({ pageIndex: true, pageSize: true })
  .extend({
    baseUrl: z.string().optional(),
  });
export type GetSchoolReportsCsvModifier = z.infer<typeof getSchoolReportsCsvModifierSchema>;

export const getSchoolReportsResultsSchema = paginatedResponse.extend({
  schoolReports: z.array(schoolReportSchema),
});
export type GetSchoolReportsResults = z.infer<typeof getSchoolReportsResultsSchema>;

export const createSchoolReportOptionsSchema = z.object({
  startedAtInstant: z.instanceof(Temporal.Instant),
  endedAtInstant: z.instanceof(Temporal.Instant),
  grade: studentYearNullableSchema,
  schoolId: z.string(),
  studentId: z.string(),
  sessionIds: z.array(z.string()),
  report: z.string(),
  reportFromAI: z.string().nullable().optional(),
  reportFromAISpanish: z.string().nullable().optional(),
});
export type CreateSchoolReportOptions = z.infer<typeof createSchoolReportOptionsSchema>;

export const updateSchoolReportOptionsSchema = z.object({
  startedAtInstant: z.instanceof(Temporal.Instant).optional(),
  endedAtInstant: z.instanceof(Temporal.Instant).optional(),
  grade: studentYearNullableSchema.optional(),
  schoolId: z.string().optional(),
  studentId: z.string().optional(),
  sessionIds: z.array(z.string()).optional(),
  report: z.string().optional(),
  reportFromAI: z.string().nullable().optional(),
  reportFromAISpanish: z.string().nullable().optional(),
});
export type UpdateSchoolReportOptions = z.infer<typeof updateSchoolReportOptionsSchema>;

export const bulkGenerateSchoolsReportsOptionsSchema = z.object({
  schoolIds: z.array(z.string()).optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
});
export type BulkGenerateSchoolsReportsOptions = z.infer<typeof bulkGenerateSchoolsReportsOptionsSchema>;

export const bulkGenerateSchoolsReportsResultsSchema = z.object({
  total: z.number(),
  processed: z.number(),
  failed: z.number(),
});
export type BulkGenerateSchoolsReportsResults = z.infer<typeof bulkGenerateSchoolsReportsResultsSchema>;

export const SchoolReportsTableColumnIds = {
  dateRange: 'dateRange',
  school: 'school',
  district: 'district',
  classroomTeacher: 'classroomTeacher',
  report: 'report',
  grade: 'grade',
  student: 'student',
  studentFirstName: 'studentFirstName',
  studentLastName: 'studentLastName',
  reportFromAI: 'reportFromAI',
  reportFromAISpanish: 'reportFromAISpanish',
  floridaStandards: 'floridaStandards',
  arkansasStandards: 'arkansasStandards',
  studentFeedbackEvents: 'studentFeedbackEvents',
  sessionAttendance: 'sessionAttendance',
} as const;

export type SchoolReportsTableColumnId = (typeof SchoolReportsTableColumnIds)[keyof typeof SchoolReportsTableColumnIds];
